import { ArrowUpRight, Building2, UtensilsCrossed, Package } from "lucide-react";
import { FadeUp, Overline } from "@/components/Motion";
import { USE_CASES, DOMESTIC_STATES, EXPORT_MARKETS, SITE, buildWhatsAppLink } from "@/data/site";
import { useLang, TA, u } from "@/data/i18n";

const ICONS = [Building2, UtensilsCrossed, Package];

export default function Presence() {
  const { lang } = useLang();
  return (
    <section id="presence" data-testid="presence-section" className="noise-overlay relative py-24 md:py-32 bg-leaf text-white overflow-hidden">
      <span aria-hidden className="text-stroke-white pointer-events-none select-none absolute top-6 right-0 font-display font-black text-[16vw] leading-none tracking-tighter whitespace-nowrap">
        GLOBAL
      </span>

      <div className="relative max-w-[1500px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          <div className="md:col-span-5">
              <FadeUp>
                <Overline dark>{u(lang, "presence_overline")}</Overline>
                <h2 className="mt-4 font-display font-black tracking-tight text-4xl md:text-5xl leading-[1.02]">
                  {lang === "ta" ? (
                    <span><em className="font-serif italic font-medium text-sun">HoReCa</em> {u(lang, "presence_heading_b")}</span>
                  ) : (
                    <span>Built for the <em className="font-serif italic font-medium text-sun">HoReCa</em> trade.</span>
                  )}
                </h2>
              </FadeUp>
            <div className="mt-12 flex flex-col gap-8">
              {USE_CASES.map((uc, i) => {
                const Icon = ICONS[i];
                return (
                  <FadeUp key={uc.title} delay={0.08 * i}>
                    <div data-testid={`usecase-${i}`} className="flex gap-5 border-l-2 border-sun/60 pl-6">
                      <Icon size={26} className="text-sun shrink-0 mt-1" />
                      <div>
                        <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight">{lang === "ta" ? TA.useCases[i].title : uc.title}</h3>
                        <p className="mt-2 text-white/65 leading-relaxed text-sm md:text-base">{lang === "ta" ? TA.useCases[i].body : uc.body}</p>
                      </div>
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-7">
            <FadeUp delay={0.1}>
              <Overline dark>{u(lang, "domestic")}</Overline>
              <div className="mt-5 flex flex-wrap gap-3">
                {DOMESTIC_STATES.map((s, i) => (
                  <span
                    key={s}
                    data-testid={`state-${s.toLowerCase().replace(/\s/g, "-")}`}
                    className="border border-white/25 px-4 py-2.5 text-sm md:text-base font-semibold tracking-wide transition-colors duration-300 hover:bg-sun hover:text-leaf hover:border-sun"
                  >
                    {lang === "ta" ? TA.states[i] : s}
                  </span>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="mt-14">
                <Overline dark>{u(lang, "export")}</Overline>
                <div className="mt-5 flex flex-col border-t border-white/15">
                  {EXPORT_MARKETS.map((m, i) => (
                    <div
                      key={m}
                      data-testid={`export-${m.toLowerCase().replace(/\s/g, "-")}`}
                      className="group flex items-center justify-between border-b border-white/15 py-5 transition-colors duration-300 hover:bg-white/5"
                    >
                      <span className="font-display font-bold text-2xl md:text-4xl tracking-tight">{m}</span>
                      <span className="text-xs tracking-[0.25em] uppercase text-white/50 group-hover:text-sun transition-colors duration-300">
                        {String(i + 1).padStart(2, "0")} — {u(lang, "import_ready")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>

            <FadeUp delay={0.3}>
              <a
                data-testid="export-enquiry-btn"
                href={buildWhatsAppLink("Hello Lovely Foodpacks! We are an importer interested in export pricing & catalogue.")}
                target="_blank"
                rel="noreferrer"
                className="group mt-10 inline-flex items-center gap-2 bg-sun text-leaf px-7 py-4 font-bold tracking-wide transition-colors duration-300 hover:bg-white"
              >
                {u(lang, "export_cta")}
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}
